/**
 * Two-pass menu analyzer with streaming support.
 * Pass 1: Stream items from Google AI → fire onItem callback as each is parsed
 * Pass 2: Enrich with descriptions/allergens in background
 */

const OPENROUTER_API_KEY = import.meta.env.VITE_OPENROUTER_API_KEY as string | undefined;
const GOOGLE_AI_KEY = import.meta.env.VITE_GOOGLE_AI_KEY as string | undefined;

const FAST_PROMPT = `You are a menu OCR engine. Extract FOOD AND DRINK items from the menu image.
Return ONLY valid JSON:
{"isMenu":true,"restaurantName":"Name or null","detectedLanguage":"en","menuType":"standard","items":[{"name":"Dish Name","originalName":"as written","price":"$12.99","category":"Appetizers","sectionHeader":false,"isModifier":false,"variantOptions":""}]}

If NOT a menu: {"isMenu":false,"items":[],"restaurantName":null,"detectedLanguage":"en","menuType":"not-a-menu"}
Section headers: {"name":"Section","sectionHeader":true,"isModifier":false}
Translate non-English names to English in "name". Keep original in "originalName".
IMPORTANT: For named/classic dishes (e.g. Biff Rydberg, Wiener Schnitzel, Pad Thai, Crème Brûlée), keep the proper name and only translate the description. Do NOT re-interpret or substitute ingredients — translate what is written, not what you think the dish contains.
VARIANT GROUPING (critical): When ONE named dish offers several protein or size choices that each have their own price (e.g. a soup or curry with separate prices for Shrimp/Räkor, Beef/Biff, Chicken/Kyckling, Tofu, Pork/Fläsk listed underneath it), output a SINGLE item for the main dish — never one item per protein. Set "name" to the real dish name (e.g. "Tom Kha Kai", "Penang Curry"), keep its base dish description in "description", and put the choices with prices in the "variantOptions" field as a short comma-separated list (e.g. "shrimp 119kr, beef 109kr, chicken 105kr, tofu 95kr, pork 105kr"). Leave "variantOptions" as "" for dishes with no protein/size choices. Use the first or lowest price as "price". NEVER output the protein/size options as standalone dishes (a card just called "Beef" or "Chicken" is wrong), and NEVER turn the main dish name into a section header.
Extract ALL food and drink items. SKIP non-food entries like: booking info, phone numbers, email addresses, website URLs, opening hours, reservation instructions, payment info, QR codes, social media handles, legal notices, and any text that is not a food or drink item.
No preamble.`;

// Client-side filter patterns for non-food items that slip through the parser
const NON_FOOD_PATTERNS = [
  /\b(?:book(?:ing)?|reserv|boka)\b/i,
  /\b(?:call|ring|tel|phone|telefon)\b/i,
  /\b(?:email|e-mail|mail[ae]?)\b/i,
  /(?:@[a-z])/i,
  /\b(?:https?:\/\/|www\.|.com|.se|.net|.org)\b/i,
  /\b\d{3,4}[\s-]?\d{3,4}[\s-]?\d{2,4}\b/,  // phone numbers
  /\b(?:follow|instagram|facebook|snapchat|tiktok)\b/i,
  /\b(?:wifi|password|lösenord|öppet|stäng|open(?:ing)?\s*hour|close[ds]?)\b/i,
  /\b(?:allerg|fråga|ask\s*(?:your|our)|informera)\b/i,
];

function isLikelyFood(item: any): boolean {
  if (item.sectionHeader || item.isModifier) return true; // pass through headers/modifiers
  const name = (item.name || "").toLowerCase();
  const original = (item.originalName || "").toLowerCase();
  const text = `${name} ${original}`;
  return !NON_FOOD_PATTERNS.some(p => p.test(text));
}

const ENRICH_PROMPT = `Given these dish names, return a JSON array:
[{"description":"brief 1-line","allergens":["gluten"],"dietaryTags":["vegetarian"],"calories":450}]
Allergens: nuts,gluten,dairy,shellfish,eggs,soy,fish,sesame,wheat.
Tags: vegetarian,vegan,spicy,gluten-free,halal,keto.
Same order as input. JSON only.`;

export interface AnalyzeMenuResult {
  items: any[];
  detectedLanguage: string;
  restaurantName: string | null;
  menuType: string;
  isMenu: boolean;
  parsingStrategy: string;
  averageConfidence: number | null;
  error?: string;
}

export interface StreamCallbacks {
  onItem?: (item: any, index: number) => void;
  onRestaurantName?: (name: string) => void;
  onComplete?: (result: AnalyzeMenuResult) => void;
}

function downscaleImage(base64: string, maxDim = 720): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const { width, height } = img;
      if (width <= maxDim && height <= maxDim) { resolve(base64); return; }
      const scale = maxDim / Math.max(width, height);
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(width * scale);
      canvas.height = Math.round(height * scale);
      const ctx = canvas.getContext("2d")!;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", 0.80).split(",")[1]);
    };
    img.onerror = () => resolve(base64);
    img.src = `data:image/jpeg;base64,${base64}`;
  });
}

function normalizeItem(item: any): any {
  // Build nutrition from explicit nutrition object OR top-level calories field
  const nutrition = item.nutrition || (item.calories ? { calories: item.calories } : null);
  return {
    name: item.name || "",
    originalName: item.originalName || item.name || "",
    description: item.description || "",
    variantOptions: item.variantOptions || "",
    price: item.price || "",
    currency: item.currency || "",
    category: item.category || "",
    allergens: item.allergens || [],
    nutrition,
    dietaryTags: item.dietaryTags || [],
    confidence: item.confidence ?? 0.8,
    isModifier: item.isModifier || false,
    modifierGroupId: item.modifierGroupId || null,
    sectionHeader: item.sectionHeader || false,
  };
}

/** Extract complete JSON objects from a partial JSON stream */
function extractItemsFromPartial(jsonSoFar: string): { items: any[]; restaurantName: string | null; meta: any } {
  const items: any[] = [];
  let restaurantName: string | null = null;
  let meta: any = {};

  // Try to extract restaurantName
  const rnMatch = jsonSoFar.match(/"restaurantName"\s*:\s*"([^"]+)"/);
  if (rnMatch) restaurantName = rnMatch[1];

  // Extract detectedLanguage, menuType
  const langMatch = jsonSoFar.match(/"detectedLanguage"\s*:\s*"([^"]+)"/);
  const typeMatch = jsonSoFar.match(/"menuType"\s*:\s*"([^"]+)"/);
  const isMenuMatch = jsonSoFar.match(/"isMenu"\s*:\s*(true|false)/);
  if (langMatch) meta.detectedLanguage = langMatch[1];
  if (typeMatch) meta.menuType = typeMatch[1];
  if (isMenuMatch) meta.isMenu = isMenuMatch[1] === "true";

  // Find the items array and extract complete objects
  const itemsStart = jsonSoFar.indexOf('"items"');
  if (itemsStart === -1) return { items, restaurantName, meta };

  const arrayStart = jsonSoFar.indexOf("[", itemsStart);
  if (arrayStart === -1) return { items, restaurantName, meta };

  // Extract complete {...} objects from the array
  let depth = 0;
  let objStart = -1;
  for (let i = arrayStart + 1; i < jsonSoFar.length; i++) {
    const ch = jsonSoFar[i];
    if (ch === "{" && depth === 0) { objStart = i; depth = 1; }
    else if (ch === "{") depth++;
    else if (ch === "}" && depth === 1) {
      depth = 0;
      const objStr = jsonSoFar.substring(objStart, i + 1);
      try {
        items.push(JSON.parse(objStr));
      } catch { /* incomplete object */ }
    }
    else if (ch === "}") depth--;
  }

  return { items, restaurantName, meta };
}

/** Streaming menu analysis — fires onItem as items are parsed */
export async function analyzeMenuStreaming(
  base64Image: string,
  callbacks: StreamCallbacks,
): Promise<AnalyzeMenuResult> {
  if (!GOOGLE_AI_KEY && !OPENROUTER_API_KEY) {
    return { items: [], detectedLanguage: "English", restaurantName: null, menuType: "unknown", isMenu: true, parsingStrategy: "no-api-key", averageConfidence: null, error: "API_ERROR: No API key configured" };
  }

  const t0 = Date.now();
  const smallBase64 = await downscaleImage(base64Image);
  console.log(`[menuAnalyzer] Downscaled in ${Date.now() - t0}ms`);

  // Try streaming with Google AI Studio
  if (GOOGLE_AI_KEY) {
    try {
      const t1 = Date.now();
      const url = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-lite:streamGenerateContent?alt=sse&key=${GOOGLE_AI_KEY}`;
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contents: [{
            role: "user",
            parts: [
              { inlineData: { mimeType: "image/jpeg", data: smallBase64 } },
              { text: FAST_PROMPT },
            ],
          }],
          generationConfig: {
            responseMimeType: "application/json",
            temperature: 0.0,
          },
        }),
      });

      if (!response.ok) {
        const err = await response.text();
        console.warn(`[menuAnalyzer] Google streaming failed: ${response.status}`, fmtErr(err));
        throw new Error(`Google AI ${response.status}`);
      }

      // Read SSE stream with safety guards
      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let fullJson = "";
      let emittedCount = 0;
      const STREAM_TIMEOUT_MS = 60_000;
      const MAX_STREAM_ITEMS = 100;
      let abortReason: string | null = null;

      while (true) {
        // Hard timeout guard
        if (Date.now() - t1 > STREAM_TIMEOUT_MS) {
          abortReason = "timeout";
          try { reader.cancel(); } catch { /* best-effort */ }
          break;
        }

        // Max-items guard
        if (emittedCount >= MAX_STREAM_ITEMS) {
          abortReason = "max-items";
          try { reader.cancel(); } catch { /* best-effort */ }
          break;
        }

        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        // Parse SSE events
        const lines = chunk.split("\n");
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = line.slice(6);
          if (data === "[DONE]") continue;
          try {
            const event = JSON.parse(data);
            const text = event.candidates?.[0]?.content?.parts?.[0]?.text || "";
            fullJson += text;

            // Try to extract items from partial JSON
            const { items, restaurantName } = extractItemsFromPartial(fullJson);

            // Emit new items (filter non-food)
            while (emittedCount < items.length) {
              if (emittedCount >= MAX_STREAM_ITEMS) break;
              const item = normalizeItem(items[emittedCount]);
              if (isLikelyFood(item)) {
                callbacks.onItem?.(item, emittedCount);
              }
              emittedCount++;
            }

            // Emit restaurant name once
            if (restaurantName && callbacks.onRestaurantName) {
              callbacks.onRestaurantName(restaurantName);
              callbacks.onRestaurantName = undefined; // only fire once
            }
          } catch { /* partial SSE data */ }
        }
      }

      if (abortReason) {
        console.warn(`[menuAnalyzer] Stream ABORTED (${abortReason}) in ${Date.now() - t1}ms, ${emittedCount} items streamed`);
      } else {
        console.log(`[menuAnalyzer] Stream complete in ${Date.now() - t1}ms, ${emittedCount} items streamed`);
      }

      // Parse final result
      let parsed: any;
      try {
        parsed = JSON.parse(fullJson);
      } catch {
        const match = fullJson.match(/\{[\s\S]*\}/);
        parsed = match ? JSON.parse(match[0]) : { items: [], isMenu: false };
      }

      const allItems = (parsed.items || []).map(normalizeItem).filter(isLikelyFood);
      const result: AnalyzeMenuResult = {
        items: allItems,
        detectedLanguage: parsed.detectedLanguage || "English",
        restaurantName: parsed.restaurantName || null,
        menuType: parsed.menuType || "unknown",
        isMenu: parsed.isMenu !== false,
        parsingStrategy: "google-streaming",
        averageConfidence: 0.8,
      };

      callbacks.onComplete?.(result);
      return result;
    } catch (e) {
      console.warn("[menuAnalyzer] Streaming failed, falling back:", fmtErr(e));
    }
  }

  // Fallback: non-streaming (OpenRouter or Google non-stream)
  return analyzeMenuDirect(base64Image);
}

/** Provider call: Google AI (returns parsed JSON or throws). */
async function callGoogleAIDirect(smallBase64: string, signal: AbortSignal): Promise<any> {
  if (!GOOGLE_AI_KEY) throw new Error("Google AI key not configured");
  const url = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-lite:generateContent?key=${GOOGLE_AI_KEY}`;
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      contents: [{ role: "user", parts: [
        { inlineData: { mimeType: "image/jpeg", data: smallBase64 } },
        { text: FAST_PROMPT },
      ]}],
      generationConfig: { responseMimeType: "application/json", temperature: 0.0 },
    }),
    signal,
  });
  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new Error(`Google AI ${response.status}${body ? `: ${body.slice(0, 200)}` : ""}`);
  }
  const data = await response.json();
  return JSON.parse(data.candidates?.[0]?.content?.parts?.[0]?.text || "{}");
}

/** Provider call: OpenRouter (returns parsed JSON or throws). */
async function callOpenRouterDirect(smallBase64: string, signal: AbortSignal): Promise<any> {
  if (!OPENROUTER_API_KEY) throw new Error("OpenRouter key not configured");
  const response = await fetch("https://openrouter.ai/api/v1/chat/completions", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${OPENROUTER_API_KEY}`,
      "Content-Type": "application/json",
      "HTTP-Referer": "https://yumz.app",
    },
    body: JSON.stringify({
      model: "google/gemini-2.5-flash",
      messages: [
        { role: "system", content: FAST_PROMPT },
        { role: "user", content: [
          { type: "text", text: "Extract all items. JSON only." },
          { type: "image_url", image_url: { url: `data:image/jpeg;base64,${smallBase64}` } },
        ]},
      ],
      temperature: 0,
      response_format: { type: "json_object" },
    }),
    signal,
  });
  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new Error(`OpenRouter ${response.status}${body ? `: ${body.slice(0, 200)}` : ""}`);
  }
  const data = await response.json();
  const content = data.choices?.[0]?.message?.content || "{}";
  return JSON.parse(content);
}

/** Non-streaming fallback — Google first, OpenRouter on any failure. */
export async function analyzeMenuDirect(base64Image: string): Promise<AnalyzeMenuResult> {
  if (!GOOGLE_AI_KEY && !OPENROUTER_API_KEY) {
    return { items: [], detectedLanguage: "English", restaurantName: null, menuType: "unknown", isMenu: true, parsingStrategy: "no-api-key", averageConfidence: null, error: "API_ERROR: No API key configured" };
  }

  const t0 = Date.now();
  const smallBase64 = await downscaleImage(base64Image);

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 60000);

  let parsed: any = null;
  let strategy = "";
  const errors: string[] = [];

  // Try Google first if configured
  if (GOOGLE_AI_KEY) {
    try {
      parsed = await callGoogleAIDirect(smallBase64, controller.signal);
      strategy = "google-direct";
    } catch (e) {
      const msg = String(e);
      errors.push(`google: ${msg}`);
      console.warn(`[menuAnalyzer] Google direct failed: ${msg}`);
    }
  }

  // Cross-provider fallback to OpenRouter on any Google failure
  if (!parsed && OPENROUTER_API_KEY) {
    try {
      parsed = await callOpenRouterDirect(smallBase64, controller.signal);
      strategy = "openrouter-fallback";
      console.log(`[menuAnalyzer] OpenRouter fallback succeeded`);
    } catch (e) {
      const msg = String(e);
      errors.push(`openrouter: ${msg}`);
      console.warn(`[menuAnalyzer] OpenRouter direct failed: ${msg}`);
    }
  }

  clearTimeout(timeout);

  if (!parsed) {
    const combined = errors.join(" | ") || "All providers failed";
    return {
      items: [],
      detectedLanguage: "English",
      restaurantName: null,
      menuType: "unknown",
      // Distinguish API failure from "real not a menu": leave isMenu undefined-ish
      // so the UI can surface a network/API error toast rather than misleading text.
      isMenu: true,
      parsingStrategy: "api-error",
      averageConfidence: null,
      error: `API_ERROR: ${combined}`,
    };
  }

  console.log(`[menuAnalyzer] Done in ${Date.now() - t0}ms (${strategy})`);
  return {
    items: (parsed.items || []).map(normalizeItem).filter(isLikelyFood),
    detectedLanguage: parsed.detectedLanguage || "English",
    restaurantName: parsed.restaurantName || null,
    menuType: parsed.menuType || "unknown",
    isMenu: parsed.isMenu !== false,
    parsingStrategy: strategy,
    averageConfidence: 0.8,
  };
}

/** Pass 2: Enrich items with descriptions, allergens, dietary tags */
export async function enrichMenuItems(items: any[]): Promise<any[]> {
  const dishItems = items.filter(i => !i.sectionHeader && !i.isModifier);
  if (!dishItems.length) return items;

  const names = dishItems.map(i => i.name);
  const prompt = `${ENRICH_PROMPT}\n\nDishes: ${JSON.stringify(names)}`;

  try {
    let enriched: any[];

    if (GOOGLE_AI_KEY) {
      const url = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-lite:generateContent?key=${GOOGLE_AI_KEY}`;
      const resp = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contents: [{ role: "user", parts: [{ text: prompt }] }],
          generationConfig: { responseMimeType: "application/json", temperature: 0.0 },
        }),
      });
      if (!resp.ok) return items;
      const data = await resp.json();
      const parsed = JSON.parse(data.candidates?.[0]?.content?.parts?.[0]?.text || "[]");
      enriched = Array.isArray(parsed) ? parsed : parsed.items || parsed.dishes || [];
    } else if (OPENROUTER_API_KEY) {
      const resp = await fetch("https://openrouter.ai/api/v1/chat/completions", {
        method: "POST",
        headers: { Authorization: `Bearer ${OPENROUTER_API_KEY}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "google/gemini-3.1-flash-lite-preview",
          messages: [{ role: "user", content: prompt }],
          temperature: 0,
          response_format: { type: "json_object" },
        }),
      });
      if (!resp.ok) return items;
      const data = await resp.json();
      const parsed = JSON.parse(data.choices?.[0]?.message?.content || "[]");
      enriched = Array.isArray(parsed) ? parsed : parsed.items || [];
    } else return items;

    let idx = 0;
    return items.map(item => {
      if (item.sectionHeader || item.isModifier) return item;
      const e = enriched[idx++];
      if (!e) return item;
      return {
        ...item,
        description: e.description || item.description || "",
        allergens: e.allergens || item.allergens || [],
        dietaryTags: e.dietaryTags || item.dietaryTags || [],
        nutrition: e.calories ? { calories: e.calories } : item.nutrition,
      };
    });
  } catch (err) {
    console.warn("[enrichMenu] Error:", fmtErr(err));
    return items;
  }
}
